import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import Overlay from './Overlay';
import {
  slideInRight,
  slideOutRight,
  slideInLeft,
  slideOutLeft,
  slideInTop,
  slideOutTop,
  slideInBottom,
  slideOutBottom,
  fadeIn,
  fadeOut,
} from '../../../styling/mixins/keyframes';

const Container = styled.div`
  z-index: 20;
  ${({ activity }) => (activity === 'closed' ? `display: none;` : ``)}
`;

const LeftRightCSS = ({ maxWidth }) => css`
  height: 100%;
  max-width: ${maxWidth || 'none'};
  top: 0;
  width: 85%;
  overflow-y: scroll;
`;

const TopBottomCSS = ({ maxHeight }) => css`
  max-height: ${maxHeight || 'none'};
  width: 100%;
  height: 100%;
  overflow-y: hidden;
  left: 0;
  right: 0;
`;

export const Drawer = styled.nav`
  ${({ direction }) => `${direction}: 0;`} animation-fill-mode: forwards;
  animation: ${getAnimation};
  background: white;
  box-shadow: 0 0 0.5rem rgba(0, 0, 0, 0.5);
  overflow-x: hidden;
  position: fixed;
  will-change: transform;
  z-index: 10;

  -ms-overflow-style: none
  &::-webkit-scrollbar {
    display: none;
  }

  ${({ direction }) =>
    direction === 'top' || direction === 'bottom'
      ? TopBottomCSS
      : LeftRightCSS};
`;

export default class TakeoverDrawer extends Component {
  static propTypes = {
    children: PropTypes.any,
    direction: PropTypes.oneOf(['top', 'bottom', 'left', 'right']).isRequired,
    maxHeight: PropTypes.string,
    maxWidth: PropTypes.string,
    open: PropTypes.bool,
    finishedClosing: PropTypes.func,
    overlayClick: PropTypes.func,
    overlayColor: PropTypes.string,
  };

  static defaultProps = {
    open: false,
  };

  state = {
    activity: 'closed',
  };

  animationEnded = () => {
    if (this.state.activity === 'closing') {
      this.setState({ activity: 'closed' });
      if (this.props.finishedClosing) {
        this.props.finishedClosing();
      }
    } else if (this.state.activity === 'opening') {
      this.setState({ activity: 'open' });
    }
  };

  overlayAnimation = activity => {
    if (activity === 'opening') {
      return css`
        ${fadeIn} .2s linear;
      `;
    } else if (activity === 'closing') {
      return css`
        ${fadeOut} .2s linear;
      `;
    }
  };

  componentDidMount() {
    if (this.props.open) {
      this.setState({ activity: 'open' });
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { open } = nextProps;
    const { activity } = prevState;
    // Capture both 'open' and 'opening'...
    const isOpen = activity.indexOf('open') > -1;
    const isClosing = isOpen && !open;
    const isOpening = !isOpen && open;

    let state = null;
    if (isOpening) {
      state = { activity: 'opening' };
    } else if (isClosing) {
      state = { activity: 'closing' };
    }

    return state;
  }

  render() {
    const { activity } = this.state;
    const {
      children,
      className,
      direction,
      maxHeight,
      maxWidth,
      overlayColor,
      overlayClick,
    } = this.props;

    return (
      <Container className={className} activity={activity}>
        <Overlay
          animation={this.overlayAnimation(activity)}
          background={overlayColor}
          justifyChild="center"
          onClick={overlayClick}
        />
        <Drawer
          animationState={activity}
          direction={direction}
          maxHeight={maxHeight}
          maxWidth={maxWidth}
          onAnimationEnd={this.animationEnded}
        >
          {children}
        </Drawer>
      </Container>
    );
  }
}

function getAnimation({ animationState, direction }) {
  let openAnimation, closeAnimation;

  if (direction === 'top') {
    openAnimation = slideInTop;
    closeAnimation = slideOutTop;
  } else if (direction === 'bottom') {
    openAnimation = slideInBottom;
    closeAnimation = slideOutBottom;
  } else if (direction === 'right') {
    openAnimation = slideInRight;
    closeAnimation = slideOutRight;
  } else {
    openAnimation = slideInLeft;
    closeAnimation = slideOutLeft;
  }

  switch (animationState) {
    case 'opening':
      return css`.2s ${openAnimation} cubic-bezier(0.18, 0.96, 0.53, 0.97)`;
    case 'closing':
      return css`.2s ${closeAnimation} cubic-bezier(0.18, 0.96, 0.53, 0.97)`;
    default:
      return 'none';
  }
}
